// AGENT-A5: left-side categories sidebar.
//
// Sections:
//   - Header: "Categories" h3 + "(N selected)" indicator
//   - Search box (filters the list as the user types)
//   - Scrollable list of CategoryItem
//   - Footer: "Select all" / "Clear" links
//
// Data: fetched from /api/dashboard/filters via the dashboard api client
// (delegates to A9's mock when VITE_USE_MOCK=true). Loading state renders a
// lightweight skeleton; error state renders a small inline notice.
//
// AGENT-BD: verified — this sidebar consumes the categories array straight
// off the /api/dashboard/filters response. No category list is hard-coded
// in this component. Now that Agent BA's backend rewrite applies the HSQ
// page-level exclusions (Accounting, Crating, Discounts, Labor, Service,
// Freight) and filters out 999-prefixed part numbers inside
// `_build_categories`, the sidebar will automatically render the correct
// PBI-faithful list without any frontend change. Same goes for the A9 mock
// — see ./api/mock.ts (also updated by BD).

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getFilters } from "../api/client";
import { useFilterStore } from "../state/filterStore";
import { useUiStore } from "../state/uiStore";
import type { FilterCategory, FiltersResponse } from "../types";
import { CategoryItem } from "./CategoryItem";
import "./CategorySidebar.css";

export function CategorySidebar() {
  const { data, isLoading, isError } = useQuery<FiltersResponse>({
    queryKey: ["dashboard", "filters"],
    queryFn: ({ signal }) => getFilters(signal),
    staleTime: 5 * 60_000,
  });

  const search = useUiStore((s) => s.categorySearch);
  const setSearch = useUiStore((s) => s.setCategorySearch);

  const selectedCount = useFilterStore((s) => s.categories.size);
  const selectAllCategories = useFilterStore((s) => s.selectAllCategories);
  const clearCategories = useFilterStore((s) => s.clearCategories);

  const categories: FilterCategory[] = data?.categories ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.trim().toLowerCase();
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, search]);

  const filteredNames = useMemo(() => filtered.map((c) => c.name), [filtered]);

  return (
    <section className="dash-sidebar dash-card dash-card--flush">
      <header className="dash-sidebar-header">
        <h3 className="dash-h3">Categories</h3>
        {selectedCount > 0 ? (
          <span className="dash-sidebar-count dash-small">
            {`(${selectedCount} selected)`}
          </span>
        ) : null}
      </header>

      <div className="dash-sidebar-search">
        <input
          type="search"
          className="dash-control dash-sidebar-search-input"
          placeholder="Search categories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search categories"
        />
      </div>

      <div className="dash-sidebar-collabel">
        <span className="dash-sidebar-collabel-name">Category</span>
        <span
          className="dash-sidebar-collabel-count"
          title="Number of quote + order line items (records) for each category, parent product, and part number in the loaded data."
        >
          # Records
        </span>
      </div>

      <div className="dash-sidebar-list-wrap">
        {isLoading ? (
          <ul className="dash-sidebar-skeleton" aria-busy="true" aria-label="Loading categories">
            {Array.from({ length: 8 }).map((_, i) => (
              <li key={i} className="dash-sidebar-skeleton-row" />
            ))}
          </ul>
        ) : isError ? (
          <div className="dash-sidebar-error dash-small">
            Failed to load categories. Retrying in the background…
          </div>
        ) : filtered.length === 0 ? (
          <div className="dash-sidebar-empty dash-small">No categories match.</div>
        ) : (
          <ul className="dash-sidebar-list" role="list">
            {filtered.map((c) => (
              <CategoryItem key={c.name} category={c} />
            ))}
          </ul>
        )}
      </div>

      <footer className="dash-sidebar-footer">
        <button
          type="button"
          className="dash-sidebar-link"
          onClick={() => selectAllCategories(filteredNames)}
          disabled={filteredNames.length === 0}
        >
          Select all
        </button>
        <span className="dash-sidebar-sep" aria-hidden>·</span>
        <button
          type="button"
          className="dash-sidebar-link"
          onClick={() => clearCategories()}
          disabled={selectedCount === 0}
        >
          Clear
        </button>
      </footer>
    </section>
  );
}
